import { useEffect, useState } from 'react';

const CHILD_SOURCE = 'relif-widget-chat';
const HOST_SOURCE = 'relif-widget-host';
const HOST_ORIGIN_PARAM = 'hostOrigin';

type HostAction = 'toggleExpand' | 'close' | 'ready';

// The widget runs on the client's own domain, so the embedder tells us its origin when it builds
// the iframe URL. Posting to that exact origin instead of '*' means a page that lies about it just
// never receives the message.
const readHostOrigin = (): string | null => {
  const declared = new URLSearchParams(window.location.search).get(HOST_ORIGIN_PARAM);
  if (!declared) return null;
  try {
    return new URL(declared).origin;
  } catch {
    return null;
  }
};

const hostOrigin = readHostOrigin();

export const canTalkToHost = (): boolean => window.parent !== window && hostOrigin !== null;

const postToHost = (type: HostAction): void => {
  if (!hostOrigin || window.parent === window) return;
  window.parent.postMessage({ source: CHILD_SOURCE, type }, hostOrigin);
};

export const requestToggleExpand = (): void => postToHost('toggleExpand');
export const requestClose = (): void => postToHost('close');

interface HostWindowState {
  expanded: boolean;
  /** False on phone viewports, where the host already renders the chat full-bleed. */
  expandable: boolean;
}

export const useHostWindowState = (): HostWindowState => {
  const [state, setState] = useState<HostWindowState>({ expanded: false, expandable: true });

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== hostOrigin) return;
      const data = event.data as { source?: string; type?: string; expanded?: boolean; expandable?: boolean } | null;
      if (data?.source !== HOST_SOURCE || data.type !== 'expandedState') return;
      setState({ expanded: Boolean(data.expanded), expandable: data.expandable !== false });
    };
    window.addEventListener('message', handler);
    postToHost('ready');
    return () => window.removeEventListener('message', handler);
  }, []);

  return state;
};
